import { Badge } from '@kalos/ui';
import type { FC } from 'react';

import { type DerivedStatus, STATUS_LABELS } from '../types';

export const StatusBadge: FC<{
  status: DerivedStatus;
  className?: string;
  /** Text before the status label, e.g. "Add: ". */
  prefix?: string;
}> = ({ status, className, prefix }) => {
  const cls = `whitespace-nowrap ${className ?? ''}`;
  const content = `${prefix ?? ''}${STATUS_LABELS[status]}`;
  switch (status) {
    case 'active':
      return (
        <Badge variant="success" className={cls}>
          {content}
        </Badge>
      );
    case 'admin_review':
      return (
        <Badge
          className={`border-transparent bg-blue-500 text-white shadow hover:bg-blue-500/80 ${cls}`}
        >
          {content}
        </Badge>
      );
    case 'awaiting_confirmation':
      return (
        <Badge
          className={`border-transparent bg-purple-500 text-white shadow hover:bg-purple-500/80 ${cls}`}
        >
          {content}
        </Badge>
      );
    case 'not_active_yet':
      return (
        <Badge variant="outline" className={`border-sky-400 text-sky-600 ${cls}`}>
          {content}
        </Badge>
      );
    case 'pending_payment':
      return (
        <Badge variant="warning" className={cls}>
          {content}
        </Badge>
      );
    case 'scheduled_cancel':
      return (
        <Badge variant="secondary" className={cls}>
          {content}
        </Badge>
      );
    case 'cancelled':
      return (
        <Badge variant="secondary" className={cls}>
          {content}
        </Badge>
      );
    // Outline slate, not the grey Cancelled fill — a superseded term ended on good terms.
    case 'renewed':
      return (
        <Badge variant="outline" className={`border-slate-400 text-slate-500 ${cls}`}>
          {content}
        </Badge>
      );
    case 'expired':
      return (
        <Badge variant="outline" className={`border-gray-400 text-gray-500 ${cls}`}>
          {content}
        </Badge>
      );
    case 'unknown':
      return (
        <Badge variant="outline" className={cls}>
          {content}
        </Badge>
      );
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
};
